import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useRole } from "../context/RoleContext";
import { listMyProjects } from "../lib/architectPortalB";
import { ArrowUpRight, ArrowRight, Hammer, Clock, CheckCircle2 } from "lucide-react";
import StatusPill from "../components/StatusPill";
import { assetUrl } from "../lib/format";
import { projectNextAction } from "../lib/projectNextAction";

export default function ArchitectProjects() {
  const { user } = useRole();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!user?.id) return;
    let alive = true;
    listMyProjects(user.id)
      .then((d) => alive && setData(d))
      .catch((e) => alive && setErr(e));
    return () => {
      alive = false;
    };
  }, [user?.id]);

  // Re-bucket the API response by *urgency to the architect* rather than the
  // backend's construction/earlier/completed split. "Needs attention" groups
  // projects where the architect is the next actor (Construction Inspection,
  // Consultation). "In motion" is everything else still active. "Completed"
  // stays separate.
  const buckets = useMemo(() => {
    if (!data) return null;
    const all = [
      ...(data.in_construction || []),
      ...(data.earlier || []),
      ...(data.completed || []),
    ];
    const attention = [];
    const inMotion = [];
    const completed = [];
    all.forEach((p) => {
      const action = projectNextAction(p);
      if (action?.tone === "action") attention.push(p);
      else if (p.current_stage === "Complete") completed.push(p);
      else inMotion.push(p);
    });
    return { attention, inMotion, completed, total: all.length };
  }, [data]);

  return (
    <section
      className="animate-fade-in-up"
      data-testid="architect-projects-page"
    >
      <div className="flex items-center gap-3">
        <span
          aria-hidden
          style={{
            display: "inline-block",
            width: 28,
            height: 2,
            background: "#B14E2F",
          }}
        />
        <span className="wn-label" style={{ color: "#8F3C23" }}>
          Projects
        </span>
      </div>
      <h1
        className="mt-3"
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontSize: "clamp(28px, 4vw, 36px)",
          fontWeight: 600,
          lineHeight: 1.1,
        }}
      >
        Architect of record.
      </h1>
      <p
        className="mt-2"
        style={{
          fontFamily: '"Newsreader", Georgia, serif',
          color: "#6C6B85",
          fontSize: 15,
          lineHeight: 1.5,
          maxWidth: 620,
        }}
      >
        Every project you've signed onto, grouped by where the next move
        sits. Open a card for documents, owner messages, and the
        construction-administration workspace.
      </p>

      <div style={{ borderTop: "1px solid #D9D8E4", marginTop: 24 }} />

      {err && (
        <p
          className="mt-8"
          style={{ color: "#A23B2E" }}
          data-testid="projects-error"
        >
          Could not load your projects.
        </p>
      )}

      {!data && !err && (
        <div className="mt-6 space-y-2" data-testid="projects-loading">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="shimmer"
              style={{ height: 112, borderRadius: 10 }}
            />
          ))}
        </div>
      )}

      {data && data.total === 0 && <EmptyState />}

      {buckets && buckets.total > 0 && (
        <div className="mt-8 space-y-10" data-testid="projects-list">
          {buckets.attention.length > 0 && (
            <Group
              title="Needs your attention"
              blurb="Your turn — open the workspace or respond to the owner."
              accent="#B14E2F"
              items={buckets.attention}
            />
          )}
          {buckets.inMotion.length > 0 && (
            <Group
              title="In motion"
              blurb="Waiting on the owner or the city. You'll be pinged when the next move is yours."
              accent="#5B6196"
              items={buckets.inMotion}
            />
          )}
          {buckets.completed.length > 0 && (
            <Group
              title="Completed"
              blurb="Final inspection submitted to DBNS. Records available for download."
              accent="#4F6149"
              items={buckets.completed}
            />
          )}
        </div>
      )}
    </section>
  );
}

function EmptyState() {
  return (
    <div
      className="mt-10 wn-card"
      style={{ padding: "40px 32px", textAlign: "center" }}
      data-testid="projects-empty"
    >
      <Hammer size={36} style={{ color: "#9AA0C0", margin: "0 auto" }} />
      <h3
        className="mt-3"
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontWeight: 600,
          fontSize: 22,
          color: "#1B1F4B",
        }}
      >
        No active projects yet.
      </h3>
      <p
        style={{
          color: "#6C6B85",
          fontSize: 14,
          marginTop: 6,
          maxWidth: 480,
          margin: "8px auto 0",
          fontFamily: '"Newsreader", Georgia, serif',
        }}
      >
        Projects appear here once you activate a consultation. From the
        Inquiry inbox, open a Requested consultation, align with the owner,
        and activate it as a project.
      </p>
      <Link
        to="/architect/inquiries"
        className="wn-btn wn-btn-primary mt-6 inline-flex"
        data-testid="projects-empty-go-inquiries"
      >
        Open Inquiry inbox
        <ArrowRight size={14} />
      </Link>
    </div>
  );
}

function Group({ title, blurb, accent, items }) {
  return (
    <div data-testid={`projects-group-${title.toLowerCase().split(" ")[0]}`}>
      <div className="flex items-baseline" style={{ gap: 12 }}>
        <h2
          style={{
            fontFamily: '"Fraunces", Georgia, serif',
            fontWeight: 600,
            fontSize: 20,
            color: "#1B1F4B",
            display: "inline-flex",
            alignItems: "baseline",
            gap: 10,
          }}
        >
          <span
            aria-hidden
            style={{
              display: "inline-block",
              width: 18,
              height: 2,
              background: accent,
              alignSelf: "center",
            }}
          />
          {title}
        </h2>
        <span
          className="tabular-nums"
          style={{
            color: "#6C6B85",
            fontSize: 13,
            fontFamily: '"Nunito Sans", sans-serif',
            fontWeight: 600,
          }}
        >
          {items.length}
        </span>
      </div>
      {blurb && (
        <p
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#6C6B85",
            fontSize: 13.5,
            marginTop: 4,
          }}
        >
          {blurb}
        </p>
      )}
      <div className="mt-4 space-y-3">
        {items.map((p) => (
          <ProjectCard key={p.id} project={p} />
        ))}
      </div>
    </div>
  );
}

function ProjectCard({ project }) {
  const navigate = useNavigate();
  const action = projectNextAction(project);
  const photo = project.design?.exterior_photo
    ? assetUrl(project.design.exterior_photo)
    : null;
  const owner =
    project.owner?.organization || project.owner?.name || "—";
  return (
    <Link
      to={`/architect/projects/${project.id}`}
      className="wn-card transition-colors"
      style={{
        textDecoration: "none",
        color: "inherit",
        padding: "18px 20px",
        display: "grid",
        gridTemplateColumns:
          "84px minmax(0, 1.4fr) minmax(0, 1.3fr) auto",
        alignItems: "center",
        gap: 18,
      }}
      data-testid={`project-row-${project.id}`}
    >
      {/* Thumbnail */}
      <span
        aria-hidden
        style={{
          width: 84,
          height: 72,
          borderRadius: 6,
          backgroundColor: "#ECEBF1",
          backgroundImage: photo ? `url(${photo})` : "none",
          backgroundSize: "cover",
          backgroundPosition: "center",
          border: "1px solid #D9D8E4",
        }}
      />

      {/* Design + parcel + owner */}
      <div className="min-w-0">
        <div className="flex items-center" style={{ gap: 8, minWidth: 0 }}>
          <span
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 600,
              fontSize: 17,
              color: "#1B1F4B",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              minWidth: 0,
            }}
          >
            {project.design?.name || project.id}
          </span>
          <StatusPill
            value={project.current_stage}
            testId={`project-stage-${project.id}`}
          />
        </div>
        <div
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#6C6B85",
            fontSize: 13.5,
            marginTop: 4,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {project.parcel?.address || "Address pending"}
        </div>
        <div
          style={{
            color: "#6C6B85",
            fontSize: 12.5,
            marginTop: 4,
            fontFamily: '"Nunito Sans", sans-serif',
          }}
        >
          Owner · {owner}
        </div>
      </div>

      {/* Next action */}
      <NextActionInline action={action} />

      {/* Row chevron + (optional) inline CA shortcut */}
      <div className="flex items-center" style={{ gap: 10 }}>
        {action?.cta && project.current_stage === "Construction Inspection" && (
          <button
            type="button"
            className="wn-btn wn-btn-primary"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              navigate(action.cta.to);
            }}
            data-testid={`project-open-ca-${project.id}`}
            style={{ paddingLeft: 12, paddingRight: 12, fontSize: 12 }}
          >
            <Hammer size={12} />
            Workspace
          </button>
        )}
        <ArrowUpRight
          size={16}
          style={{ color: "#9AA0C0", flexShrink: 0 }}
          aria-hidden
        />
      </div>
    </Link>
  );
}

function NextActionInline({ action }) {
  if (!action) return <span />;
  const Icon =
    action.tone === "action"
      ? Hammer
      : action.tone === "done"
        ? CheckCircle2
        : Clock;
  const iconColor =
    action.tone === "action"
      ? "#B14E2F"
      : action.tone === "done"
        ? "#4F6149"
        : "#5B6196";
  return (
    <div
      className="min-w-0"
      data-testid={`project-next-action-${action.who}`}
    >
      <div className="inline-flex items-center" style={{ gap: 6 }}>
        <Icon size={12} style={{ color: iconColor }} strokeWidth={2.2} />
        <span
          style={{
            fontSize: 12.5,
            fontWeight: 700,
            color: "#1B1F4B",
            fontFamily: '"Nunito Sans", sans-serif',
            letterSpacing: "0.01em",
          }}
        >
          {action.headline}
        </span>
      </div>
      <div
        style={{
          fontFamily: '"Newsreader", Georgia, serif',
          fontStyle: "italic",
          color: "#6C6B85",
          fontSize: 12.5,
          marginTop: 2,
          lineHeight: 1.4,
          overflow: "hidden",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
        }}
      >
        {action.blurb}
      </div>
    </div>
  );
}
